<template>
  <div class="manager-company-features-ui">
    <VSpinner v-show="isLoading" />
    <VFallbackText v-show="failedToFetch">
      Something went wrong. <VBtn flat @click="fetch">Retry</VBtn>
    </VFallbackText>
    <ul v-if="features.length > 0" class="manager-company-features-ui__list">
      <li
        v-for="feature in features"
        :key="feature.id"
        class="manager-company-features-ui__list-item"
      >
        {{ MANAGER_FEATURE_LABELS[feature.name] ?? feature.name }}
        <DeleteBtn
          :item-description="`tool ${MANAGER_FEATURE_LABELS[feature.name] ?? feature.name}`"
          @confirm="remove(feature.id)"
        />
      </li>
    </ul>
    <VFallbackText v-else-if="!isLoading && !failedToFetch">No tools set</VFallbackText>
    <VBtn flat @click="showFeaturesModal">Add</VBtn>
  </div>
</template>
<script setup lang="ts">
  // Services
  import { ManagerFeatureDataClient } from '~/modules/manager/services/manager-feature-data-client';

  // Types
  import type { TManagerFeature } from '~/modules/manager/types';

  // Config
  import { MANAGER_FEATURE_LABELS } from '~/modules/manager/company/features/config';

  // Composables
  import { useNotifyResource } from '~/misc/resource/composables/use-notify-resource';

  // Components
  import DeleteBtn from '~/misc/resource/delete/btn/DeleteBtn.vue';
  import VBtn from '~/misc/v-btn';
  import VFallbackText from '~/misc/v-fallback-text/VFallbackText.vue';
  import VSpinner from '~/misc/v-spinner/VSpinner.vue';

  const FeaturesModal = defineAsyncComponent(
    () => import('~/modules/manager/company/features/modal/ManagerCompanyFeaturesModal.vue')
  );

  const props = defineProps({
    companyId: {
      type: Number as PropType<number | null>,
      default: null,
    },
  });

  const { show: showModal } = useModal();
  const { notifyFailure } = useNotifyResource();

  const features: Ref<TManagerFeature[]> = ref<TManagerFeature[]>([]);
  const failedToFetch: Ref<boolean> = ref<boolean>(false);
  const isLoading: Ref<boolean> = ref<boolean>(false);

  const dataClient: ManagerFeatureDataClient = new ManagerFeatureDataClient();

  const fetch = () => {
    isLoading.value = true;
    failedToFetch.value = false;

    dataClient
      .request<TManagerFeature[]>('index', props.companyId)
      .then((response: TManagerFeature[]) => {
        features.value = response;
      })
      .catch(() => {
        failedToFetch.value = true;
      })
      .finally(() => {
        isLoading.value = false;
      });
  };

  const remove = (id: number) => {
    dataClient
      .request('destroy', id)
      .then(() => {
        const index: number = features.value.findIndex(
          (feature: TManagerFeature) => feature.id === id
        );

        if (index !== -1) {
          features.value.splice(index, 1);
        }
      })
      .catch(() => {
        notifyFailure();
      });
  };

  const showFeaturesModal = () => {
    showModal(FeaturesModal, {
      companyId: props.companyId,
      dataClient,
      createdCallback: (feature: TManagerFeature) => {
        features.value.push(feature);
      },
    });
  };

  fetch();
</script>
